import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Assessment as AssessmentIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  PendingActions as PendingIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAppSelector } from '../../store';
import { PurchaseRequest, RequestStatus, RequestFrequency } from '../../types/request.types';
import { API_CONFIG } from '../../config/api';

interface DepartmentStats {
  totalRequests: number;
  approvedRequests: number;
  rejectedRequests: number;
  pendingRequests: number;
  totalAmount: number;
  approvedAmount: number;
  averageAmount: number;
}

interface Department {
  id: string;
  name: string;
}

interface Filters {
  departmentId: string;
  status: string;
  frequency: string;
  renewalMonth: string;
  fromDate: string;
  toDate: string;
  minAmount: string;
  maxAmount: string;
  searchTerm: string;
}

const DepartmentReports: React.FC = () => {
  const navigate = useNavigate();
  const user = useAppSelector(state => state.auth.user);
  const [requests, setRequests] = useState<PurchaseRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<PurchaseRequest[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [stats, setStats] = useState<DepartmentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  const [filters, setFilters] = useState<Filters>({
    departmentId: '',
    status: '',
    frequency: '',
    renewalMonth: '',
    fromDate: '',
    toDate: '',
    minAmount: '',
    maxAmount: '',
    searchTerm: '',
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [requests, filters]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all requests
      const requestsResponse = await fetch(`${API_CONFIG.BASE_URL}/requests`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!requestsResponse.ok) {
        throw new Error('Failed to fetch requests');
      }

      const allRequests: PurchaseRequest[] = await requestsResponse.json();

      // Fetch departments
      const departmentsResponse = await fetch(`${API_CONFIG.BASE_URL}/users/departments`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (departmentsResponse.ok) {
        const departmentData = await departmentsResponse.json();
        setDepartments(departmentData); // Already has {id, name} structure
      }

      setRequests(allRequests);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...requests];

    // Department filter
    if (filters.departmentId) {
      filtered = filtered.filter(request => request.departmentId === filters.departmentId);
    }

    // Status filter
    if (filters.status) {
      filtered = filtered.filter(request => request.status === filters.status);
    }

    // Frequency filter
    if (filters.frequency) {
      filtered = filtered.filter(request => request.frequency === filters.frequency);
    }

    // Renewals in Month filter (for subscription tracking)
    if (filters.renewalMonth) {
      filtered = filtered.filter(request => {
        // Only include recurring subscriptions (MONTHLY, YEARLY)
        // Handle missing frequency data gracefully - if frequency is missing, include it
        const isRecurring = !request.frequency || 
                           request.frequency === RequestFrequency.MONTHLY || 
                           request.frequency === RequestFrequency.YEARLY;
        
        if (!isRecurring) {
          return false;
        }
        
        // Check if the delivery month matches the selected renewal month
        const deliveryDate = new Date(request.requestedDeliveryDate);
        const deliveryMonth = deliveryDate.getMonth(); // 0-11
        const selectedMonth = parseInt(filters.renewalMonth); // 0-11
        
        return deliveryMonth === selectedMonth;
      });
    }

    // Date range filter (by delivery date)
    if (filters.fromDate) {
      filtered = filtered.filter(request => 
        new Date(request.requestedDeliveryDate) >= new Date(filters.fromDate)
      );
    }

    if (filters.toDate) {
      filtered = filtered.filter(request => 
        new Date(request.requestedDeliveryDate) <= new Date(filters.toDate + 'T23:59:59')
      );
    }

    // Amount range filter
    if (filters.minAmount) {
      filtered = filtered.filter(request => request.amount >= parseFloat(filters.minAmount));
    }

    if (filters.maxAmount) {
      filtered = filtered.filter(request => request.amount <= parseFloat(filters.maxAmount));
    }

    // Search term filter (description, title, requester)
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(request => 
        request.description.toLowerCase().includes(searchLower) ||
        request.title.toLowerCase().includes(searchLower) ||
        request.requester?.firstName.toLowerCase().includes(searchLower) ||
        request.requester?.lastName.toLowerCase().includes(searchLower)
      );
    }

    setFilteredRequests(filtered);
    calculateStats(filtered);
  };

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      departmentId: '',
      status: '',
      frequency: '',
      renewalMonth: '',
      fromDate: '',
      toDate: '',
      minAmount: '',
      maxAmount: '',
      searchTerm: '',
    });
  };

  const getActiveFiltersCount = () => {
    return Object.values(filters).filter(value => value !== '').length;
  };

  const calculateStats = (departmentRequests: PurchaseRequest[]) => {
    // Exclude DRAFT requests from all calculations
    const submittedRequests = departmentRequests.filter(r => r.status !== RequestStatus.DRAFT);
    
    const approved = submittedRequests.filter(r => 
      r.status === RequestStatus.APPROVED || 
      r.status === RequestStatus.PAID || 
      r.status === RequestStatus.CLOSED
    );
    const rejected = submittedRequests.filter(r => r.status === RequestStatus.REJECTED);
    const pending = submittedRequests.filter(r => 
      r.status === RequestStatus.PENDING_MANAGER || 
      r.status === RequestStatus.PENDING_GENERAL_MANAGER ||
      r.status === RequestStatus.PENDING_SENIOR_MANAGER ||
      r.status === RequestStatus.PENDING_FINANCE ||
      r.status === RequestStatus.PENDING_CFO ||
      r.status === RequestStatus.MORE_INFO_NEEDED
    );

    const totalAmount = submittedRequests.reduce((sum, r) => sum + r.amount, 0);
    const approvedAmount = approved.reduce((sum, r) => sum + r.amount, 0);

    setStats({
      totalRequests: submittedRequests.length,
      approvedRequests: approved.length,
      rejectedRequests: rejected.length,
      pendingRequests: pending.length,
      totalAmount,
      approvedAmount,
      averageAmount: submittedRequests.length > 0 ? totalAmount / submittedRequests.length : 0,
    });
  };

  const getStatusColor = (status: RequestStatus) => {
    switch (status) {
      case RequestStatus.APPROVED:
      case RequestStatus.PAID:
      case RequestStatus.CLOSED:
        return 'success';
      case RequestStatus.REJECTED:
        return 'error';
      case RequestStatus.PENDING_MANAGER:
      case RequestStatus.PENDING_GENERAL_MANAGER:
      case RequestStatus.PENDING_SENIOR_MANAGER:
      case RequestStatus.PENDING_FINANCE:
      case RequestStatus.PENDING_CFO:
        return 'warning';
      case RequestStatus.MORE_INFO_NEEDED:
        return 'info';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: string | Date) => {
    return format(new Date(date), 'MMM dd, yyyy');
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, py: 4 }}>
      <Container maxWidth="lg">
        <Paper sx={{ p: 3 }}>
          {/* Header */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item>
              <IconButton onClick={() => navigate('/dashboard')} size="large">
                <ArrowBackIcon />
              </IconButton>
            </Grid>
            <Grid item xs>
              <Typography variant="h4" gutterBottom>
                Department Reports
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Department Purchase Request Analytics
              </Typography>
            </Grid>
          </Grid>

          {/* Advanced Filters */}
          <Accordion 
            expanded={filtersExpanded} 
            onChange={() => setFiltersExpanded(!filtersExpanded)}
            sx={{ mb: 3 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="filters-content"
              id="filters-header"
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterIcon />
                <Typography variant="h6">
                  Advanced Filters
                </Typography>
                {getActiveFiltersCount() > 0 && (
                  <Chip 
                    label={`${getActiveFiltersCount()} active`} 
                    size="small" 
                    color="primary" 
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Search Term */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Search"
                    placeholder="Search by description, requester name..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    variant="outlined"
                  />
                </Grid>

                {/* Department Filter */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Department</InputLabel>
                    <Select
                      value={filters.departmentId}
                      label="Department"
                      onChange={(e) => handleFilterChange('departmentId', e.target.value)}
                    >
                      <MenuItem value="">All Departments</MenuItem>
                      {departments.map((dept) => (
                        <MenuItem key={dept.id} value={dept.id}>
                          {dept.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Status Filter */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filters.status}
                      label="Status"
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value={RequestStatus.DRAFT}>Draft</MenuItem>
                      <MenuItem value={RequestStatus.PENDING_MANAGER}>Pending Manager</MenuItem>
                      <MenuItem value={RequestStatus.PENDING_SENIOR_MANAGER}>Pending Senior Manager</MenuItem>
                      <MenuItem value={RequestStatus.PENDING_GENERAL_MANAGER}>Pending General Manager</MenuItem>
                      <MenuItem value={RequestStatus.PENDING_CFO}>Pending CFO</MenuItem>
                      <MenuItem value={RequestStatus.PENDING_FINANCE}>Pending Finance</MenuItem>
                      <MenuItem value={RequestStatus.APPROVED}>Approved</MenuItem>
                      <MenuItem value={RequestStatus.REJECTED}>Rejected</MenuItem>
                      <MenuItem value={RequestStatus.PAID}>Paid</MenuItem>
                      <MenuItem value={RequestStatus.CLOSED}>Closed</MenuItem>
                      <MenuItem value={RequestStatus.MORE_INFO_NEEDED}>More Info Needed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Frequency Filter */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={filters.frequency}
                      label="Frequency"
                      onChange={(e) => handleFilterChange('frequency', e.target.value)}
                    >
                      <MenuItem value="">All Frequencies</MenuItem>
                      <MenuItem value={RequestFrequency.ONCE}>Once</MenuItem>
                      <MenuItem value={RequestFrequency.MONTHLY}>Monthly</MenuItem>
                      <MenuItem value={RequestFrequency.YEARLY}>Yearly</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Renewals in Month Filter */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Renewals by Delivery Month</InputLabel>
                    <Select
                      value={filters.renewalMonth}
                      label="Renewals by Delivery Month"
                      onChange={(e) => handleFilterChange('renewalMonth', e.target.value)}
                    >
                      <MenuItem value="">All Months</MenuItem>
                      <MenuItem value="0">January</MenuItem>
                      <MenuItem value="1">February</MenuItem>
                      <MenuItem value="2">March</MenuItem>
                      <MenuItem value="3">April</MenuItem>
                      <MenuItem value="4">May</MenuItem>
                      <MenuItem value="5">June</MenuItem>
                      <MenuItem value="6">July</MenuItem>
                      <MenuItem value="7">August</MenuItem>
                      <MenuItem value="8">September</MenuItem>
                      <MenuItem value="9">October</MenuItem>
                      <MenuItem value="10">November</MenuItem>
                      <MenuItem value="11">December</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Delivery Date Range */}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Delivery From Date"
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Delivery To Date"
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Amount Range */}
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Min Amount ($)"
                    type="number"
                    value={filters.minAmount}
                    onChange={(e) => handleFilterChange('minAmount', e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Max Amount ($)"
                    type="number"
                    value={filters.maxAmount}
                    onChange={(e) => handleFilterChange('maxAmount', e.target.value)}
                    inputProps={{ min: 0, step: 0.01 }}
                  />
                </Grid>

                {/* Filter Actions */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={clearFilters}
                      disabled={getActiveFiltersCount() === 0}
                    >
                      Clear Filters
                    </Button>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                      Showing {filteredRequests.length} of {requests.length} requests
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {stats && (
            <>
              {/* Statistics Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography color="text.secondary" gutterBottom>
                            Total Requests
                          </Typography>
                          <Typography variant="h4">
                            {stats.totalRequests}
                          </Typography>
                        </Box>
                        <AssessmentIcon color="primary" sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography color="text.secondary" gutterBottom>
                            Approved
                          </Typography>
                          <Typography variant="h4" color="success.main">
                            {stats.approvedRequests}
                          </Typography>
                        </Box>
                        <ApprovedIcon color="success" sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography color="text.secondary" gutterBottom>
                            Rejected
                          </Typography>
                          <Typography variant="h4" color="error.main">
                            {stats.rejectedRequests}
                          </Typography>
                        </Box>
                        <RejectedIcon color="error" sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography color="text.secondary" gutterBottom>
                            Pending
                          </Typography>
                          <Typography variant="h4" color="warning.main">
                            {stats.pendingRequests}
                          </Typography>
                        </Box>
                        <PendingIcon color="warning" sx={{ fontSize: 40 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Financial Summary */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Total Amount Requested
                      </Typography>
                      <Typography variant="h5" color="primary">
                        {formatCurrency(stats.totalAmount)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Approved Amount
                      </Typography>
                      <Typography variant="h5" color="success.main">
                        {formatCurrency(stats.approvedAmount)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Average Request
                      </Typography>
                      <Typography variant="h5" color="info.main">
                        {formatCurrency(stats.averageAmount)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Approval Rate */}
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Department Performance
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main">
                          {stats.totalRequests > 0 ? Math.round((stats.approvedRequests / stats.totalRequests) * 100) : 0}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Approval Rate
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="error.main">
                          {stats.totalRequests > 0 ? Math.round((stats.rejectedRequests / stats.totalRequests) * 100) : 0}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Rejection Rate
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="warning.main">
                          {stats.totalRequests > 0 ? Math.round((stats.pendingRequests / stats.totalRequests) * 100) : 0}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Pending Rate
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}

          {/* Recent Requests Table */}
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Department Requests
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredRequests.length > 10 && `Showing first 10 of ${filteredRequests.length} results`}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {filteredRequests.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  {requests.length === 0 
                    ? "No requests found."
                    : "No requests match your current filters. Try adjusting the filter criteria."
                  }
                </Typography>
              ) : (
                <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Requester</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Last Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRequests.slice(0, 10).map((request) => (
                        <TableRow key={request.id}>
                          <TableCell>
                            <Typography variant="subtitle2">
                              {request.title}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {request.requester.firstName} {request.requester.lastName}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(request.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.status.replace(/_/g, ' ')}
                              color={getStatusColor(request.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {formatDate(request.createdAt)}
                          </TableCell>
                          <TableCell>
                            {formatDate(request.updatedAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {filteredRequests.length > 10 && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Button 
                      variant="outlined" 
                      size="small"
                      onClick={() => setFiltersExpanded(true)}
                    >
                      View All ({filteredRequests.length}) Results
                    </Button>
                  </Box>
                )}
              </>
              )}
            </CardContent>
          </Card>
        </Paper>
      </Container>
    </Box>
  );
};

export default DepartmentReports; 